import { jsPDF } from 'jspdf'
import {
  ACTION_CHECKLIST,
  CONTACT,
  DISCLAIMER,
  EMPLOYMENT_OPTIONS,
  TIMEFRAME_OPTIONS,
} from '../data/roadmapContent'
import { formatCurrency, formatDate, reviewDate } from './roadmapCalcs'
import { calculatePillars } from './savingsPillars'
import type { RoadmapAnswers, RoadmapResults } from '../types/roadmap'

const LOGO_URL =
  'https://cdn.magicpatterns.com/uploads/miVDHRURGJeqgpYC7Z9kCF/logo.png'

// The PDF always prints on white with dark text, whatever theme is on screen.
const TEAL = {
  r: 20,
  g: 184,
  b: 166,
}
const INK = {
  r: 15,
  g: 23,
  b: 42,
}
const MUTED = {
  r: 100,
  g: 116,
  b: 139,
}
const RULE = {
  r: 214,
  g: 222,
  b: 232,
}

const PAGE_W = 210
const PAGE_H = 297
const MARGIN = 16
const CONTENT_W = PAGE_W - MARGIN * 2

/** Printed height of the logo. Its width is derived from the file's real
 *  aspect ratio so the mark is never distorted. */
const LOGO_HEIGHT = 15
const LOGO_MAX_WIDTH = 60

/**
 * Load the logo as a data URL so jsPDF can embed it. If the CDN doesn't allow
 * cross-origin reads, we resolve null and fall back to a typeset wordmark
 * rather than failing the whole download.
 */
async function loadLogo(): Promise<string | null> {
  try {
    const response = await fetch(LOGO_URL, {
      mode: 'cors',
    })
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

function timeframeLabel(answers: RoadmapAnswers): string {
  return (
    TIMEFRAME_OPTIONS.find((option) => option.value === answers.timeframe)
      ?.label ?? 'Not set'
  )
}

function employmentLabel(value: string | null): string {
  return (
    EMPLOYMENT_OPTIONS.find((option) => option.value === value)?.label ??
    'Not set'
  )
}

export async function generateRoadmapPdf(
  answers: RoadmapAnswers,
  results: RoadmapResults,
): Promise<jsPDF> {
  const doc = new jsPDF({
    unit: 'mm',
    format: 'a4',
  })
  const generated = new Date()
  const logo = await loadLogo()

  // ---- shared helpers -----------------------------------------------------

  // Read the logo's real pixel dimensions and scale from those, so the mark is
  // never stretched or squashed to fit an assumed box. Height is fixed and the
  // width follows from the true aspect ratio.
  const logoBox = (() => {
    if (!logo) return null
    try {
      const props = doc.getImageProperties(logo)
      if (!props.width || !props.height) return null
      const height = LOGO_HEIGHT
      const width = height * (props.width / props.height)
      // A very wide wordmark would otherwise run into the date on the right.
      if (width > LOGO_MAX_WIDTH) {
        return {
          width: LOGO_MAX_WIDTH,
          height: LOGO_MAX_WIDTH * (props.height / props.width),
        }
      }
      return {
        width,
        height,
      }
    } catch {
      return null
    }
  })()

  const header = async (subtitle: string) => {
    let y = MARGIN
    if (logo && logoBox) {
      doc.addImage(logo, 'PNG', MARGIN, y, logoBox.width, logoBox.height)
    } else {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(15)
      doc.setTextColor(TEAL.r, TEAL.g, TEAL.b)
      doc.text('SCALE', MARGIN, y + 8)
      doc.setFontSize(8)
      doc.text('FINANCE STRATEGIES', MARGIN, y + 13)
    }

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(`Generated ${formatDate(generated)}`, PAGE_W - MARGIN, y + 6, {
      align: 'right',
    })
    doc.text('All figures are estimates', PAGE_W - MARGIN, y + 11, {
      align: 'right',
    })

    y += 21
    doc.setDrawColor(RULE.r, RULE.g, RULE.b)
    doc.setLineWidth(0.4)
    doc.line(MARGIN, y, PAGE_W - MARGIN, y)

    y += 10
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(19)
    doc.setTextColor(INK.r, INK.g, INK.b)
    doc.text('Your Property Savings Roadmap', MARGIN, y)

    y += 7
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10.5)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(subtitle, MARGIN, y)

    return y + 10
  }

  const footer = (page: number) => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(
      `${CONTACT.name} · ${CONTACT.acn} · Estimates only, generated ${formatDate(generated)}`,
      MARGIN,
      PAGE_H - 10,
    )
    doc.text(`Page ${page} of 2`, PAGE_W - MARGIN, PAGE_H - 10, {
      align: 'right',
    })
  }

  const sectionTitle = (label: string, y: number) => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(TEAL.r, TEAL.g, TEAL.b)
    doc.text(label.toUpperCase(), MARGIN, y)
    doc.setDrawColor(RULE.r, RULE.g, RULE.b)
    doc.setLineWidth(0.3)
    doc.line(MARGIN, y + 2.5, PAGE_W - MARGIN, y + 2.5)
    return y + 9
  }

  const row = (label: string, value: string, y: number, emphasis = false) => {
    doc.setFont('helvetica', emphasis ? 'bold' : 'normal')
    doc.setFontSize(emphasis ? 12 : 10.5)
    doc.setTextColor(emphasis ? INK.r : MUTED.r, emphasis ? INK.g : MUTED.g, emphasis ? INK.b : MUTED.b)
    doc.text(label, MARGIN, y)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(INK.r, INK.g, INK.b)
    doc.text(value, PAGE_W - MARGIN, y, {
      align: 'right',
    })
    return y + (emphasis ? 8 : 6.8)
  }

  // ---- page 1: the numbers ------------------------------------------------

  let y = await header(
    answers.name ? `Prepared for ${answers.name}` : 'Your savings estimate',
  )

  y = sectionTitle('Your property goal', y)
  y = row(
    'Buying',
    answers.goal === 'first-investment'
      ? 'First investment property'
      : 'First home',
    y,
  )
  y = row('Target property price', formatCurrency(answers.propertyPrice), y)
  y = row('Target purchase date', timeframeLabel(answers), y)
  if (answers.goal === 'first-home') {
    y = row(
      'First home buyer',
      answers.isFirstHomeBuyer === null
        ? 'Not answered'
        : answers.isFirstHomeBuyer
          ? 'Yes'
          : 'No',
      y,
    )
  }

  y += 5
  y = sectionTitle('Your savings goal', y)
  y = row('Deposit goal', formatCurrency(answers.deposit), y)
  y = row(
    `Estimated purchasing costs${answers.purchasingCostsEstimated ? ' (typical estimate)' : ''}`,
    formatCurrency(answers.purchasingCosts),
    y,
  )
  y = row(
    `Planned cash buffer${answers.cashBufferEstimated ? ' (typical estimate)' : ''}`,
    formatCurrency(answers.cashBuffer),
    y,
  )
  if (answers.goal === 'first-investment') {
    y = row('Investor reserve', formatCurrency(answers.investorReserve), y)
  }
  if (answers.otherCosts > 0) {
    y = row('Other expected costs', formatCurrency(answers.otherCosts), y)
  }
  y += 1
  y = row('Total savings goal', formatCurrency(results.totalSavingsGoal), y, true)

  y += 5
  y = sectionTitle('Where you are now', y)
  y = row('Current usable savings', formatCurrency(results.usableSavings), y)
  y = row('Remaining savings gap', formatCurrency(results.savingsGap), y)
  y = row(
    'Amount to save each pay',
    answers.couldSavePerPay > 0
      ? `${formatCurrency(answers.couldSavePerPay)} ${answers.payFrequency ?? ''}`.trim()
      : 'Not set',
    y,
    true,
  )
  y = row(
    'Estimated completion date',
    results.completionDate ? formatDate(results.completionDate) : 'Not set',
    y,
  )

  // Progress bar, drawn as filled rectangles so it survives greyscale printing.
  y += 6
  const percent = Math.round(results.progress * 100)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(INK.r, INK.g, INK.b)
  doc.text('Savings progress', MARGIN, y)
  doc.text(`${percent}%`, PAGE_W - MARGIN, y, {
    align: 'right',
  })
  y += 3.5
  doc.setFillColor(232, 236, 241)
  doc.rect(MARGIN, y, CONTENT_W, 7, 'F')
  if (percent > 0) {
    doc.setFillColor(TEAL.r, TEAL.g, TEAL.b)
    doc.rect(MARGIN, y, (CONTENT_W * percent) / 100, 7, 'F')
  }
  doc.setDrawColor(RULE.r, RULE.g, RULE.b)
  doc.setLineWidth(0.3)
  doc.rect(MARGIN, y, CONTENT_W, 7)
  y += 14

  // The three pillars sit on page 1, directly under the progress bar: these are
  // the dates the client is meant to act on, so they belong with the numbers
  // rather than filed behind the income summary.
  const pillars = calculatePillars(
    results.savingsGap,
    answers.couldSavePerPay,
    answers.payFrequency,
    generated,
  )
  if (pillars.length > 0) {
    y = sectionTitle('Your three savings pillars', y)
    pillars.forEach((pillar) => {
      // Pillar one carries the emphasis — it is the nearest and the easiest,
      // and hitting it early is what keeps the habit going.
      const first = pillar.index === 1
      doc.setFont('helvetica', first ? 'bold' : 'normal')
      doc.setFontSize(first ? 11 : 10)
      doc.setTextColor(
        first ? INK.r : MUTED.r,
        first ? INK.g : MUTED.g,
        first ? INK.b : MUTED.b,
      )
      doc.text(
        `Pillar ${pillar.index} — save ${formatCurrency(pillar.amount)} (reach ${formatCurrency(pillar.cumulative)})`,
        MARGIN,
        y,
      )
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(INK.r, INK.g, INK.b)
      doc.text(
        pillar.date ? formatDate(pillar.date) : 'No date set',
        PAGE_W - MARGIN,
        y,
        {
          align: 'right',
        },
      )
      y += 6.2
    })

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(
      doc.splitTextToSize(
        `These are the amounts still to save, so they total your ${formatCurrency(results.savingsGap)} gap rather than the full savings goal. The first is deliberately the smallest.`,
        CONTENT_W,
      ),
      MARGIN,
      y + 1,
    )
    y += 11
  }

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
  doc.text(doc.splitTextToSize(DISCLAIMER, CONTENT_W), MARGIN, y)

  footer(1)

  // ---- page 2: the plan ---------------------------------------------------

  doc.addPage()
  y = await header('Your income, commitments and next steps')

  y = sectionTitle('Income summary', y)
  y = row(
    'Employment',
    employmentLabel(answers.applicantOne.employmentType),
    y,
  )
  y = row(
    'Gross annual income',
    formatCurrency(answers.applicantOne.grossAnnualIncome),
    y,
  )
  if (answers.applicantOne.extraIncome > 0) {
    y = row(
      'Overtime, bonuses, allowances',
      formatCurrency(answers.applicantOne.extraIncome),
      y,
    )
  }
  if (answers.applicantOne.rentalOtherIncome > 0) {
    y = row(
      'Rental or other income',
      formatCurrency(answers.applicantOne.rentalOtherIncome),
      y,
    )
  }
  if (answers.buyingWith === 'together') {
    y = row(
      'Co-buyer employment',
      employmentLabel(answers.applicantTwo.employmentType),
      y,
    )
    y = row(
      'Co-buyer gross annual income',
      formatCurrency(answers.applicantTwo.grossAnnualIncome),
      y,
    )
  }
  y = row(
    'Total household income',
    formatCurrency(results.totalHouseholdIncome),
    y,
    true,
  )

  y += 5
  y = sectionTitle('Commitments summary', y)
  y = row('Dependants', String(answers.dependants), y)

  // Only print debts that actually exist. A page of "$0" rows crowds the sheet
  // and buries the commitments that matter.
  const debtRows: [string, number][] = [
    ['Home loans owing', answers.debts.homeLoans],
    ['Car and personal loans', answers.debts.carPersonalLoans],
    ['Credit card limits', answers.debts.creditCardLimits],
    ['HECS or HELP', answers.debts.hecs],
    ['Buy Now Pay Later', answers.debts.buyNowPayLater],
  ]
  const owed = debtRows.filter(([, amount]) => amount > 0)
  if (owed.length === 0) {
    y = row('Debts declared', 'None', y)
  } else {
    owed.forEach(([label, amount]) => {
      y = row(label, formatCurrency(amount), y)
    })
  }

  y = row('Current rent', `${formatCurrency(answers.currentRent)} a month`, y)
  y = row(
    'Living expenses',
    `${formatCurrency(answers.livingExpenses)} a month`,
    y,
  )

  y += 5
  y = sectionTitle('Your action checklist', y)
  doc.setFontSize(10.5)
  ACTION_CHECKLIST.forEach((item) => {
    const ticked = answers.checkedActions.includes(item.id)
    doc.setDrawColor(INK.r, INK.g, INK.b)
    doc.setLineWidth(0.35)
    doc.rect(MARGIN, y - 3.2, 4, 4)
    if (ticked) {
      doc.setFillColor(TEAL.r, TEAL.g, TEAL.b)
      doc.rect(MARGIN + 0.7, y - 2.5, 2.6, 2.6, 'F')
    }
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(INK.r, INK.g, INK.b)
    doc.text(item.label, MARGIN + 7, y)
    y += 6.4
  })

  y += 2
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(TEAL.r, TEAL.g, TEAL.b)
  doc.text(`Review date: ${formatDate(reviewDate(generated))}`, MARGIN, y)

  y += 9
  y = sectionTitle('Notes', y)
  doc.setDrawColor(RULE.r, RULE.g, RULE.b)
  doc.setLineWidth(0.3)
  for (let i = 0; i < 4; i += 1) {
    doc.line(MARGIN, y, PAGE_W - MARGIN, y)
    y += 8
  }

  y += 2
  y = sectionTitle('Your next step', y)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(INK.r, INK.g, INK.b)
  doc.text('Book a Call With Liam', MARGIN, y)
  y += 6
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
  doc.text(
    `${CONTACT.email}   ·   ${CONTACT.phone}   ·   ${CONTACT.hours}`,
    MARGIN,
    y,
  )

  footer(2)

  return doc
}

export function roadmapFileName(answers: RoadmapAnswers): string {
  const who = answers.name
    ? answers.name.trim().replace(/[^a-z0-9]+/gi, '-').toLowerCase()
    : 'my'
  return `${who}-savings-roadmap.pdf`
}
